/* BBB Diwali landing page — interactions & motion.
   Loaded as a deferred module by each dw-* section (the browser executes it once).
   No dependencies. Scroll work is IntersectionObserver-driven; the few
   continuous effects (hero parallax, scroller tilt) run in rAF only while needed. */
const DW = (window.DW = window.DW || {});
if (!DW.booted) {
  DW.booted = true;

  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const desktop = matchMedia('(min-width: 990px)');
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const inited = new WeakMap();

  /* ---------------------------------------------------------------- zones
     A section goes "live" once it covers ≥30% of the viewport (or 70% of
     itself when shorter). Entrances play once; ambient loops only run live. */
  const TH = Array.from({ length: 21 }, (_, i) => i / 20);
  const zoneIO = new IntersectionObserver(
    (entries) => {
      const vh = innerHeight;
      for (const e of entries) {
        const need = Math.min(0.3 * vh, 0.7 * e.boundingClientRect.height);
        setLive(e.target, e.isIntersecting && e.intersectionRect.height >= need - 1);
      }
    },
    { threshold: TH }
  );
  const nearIO = new IntersectionObserver(
    (entries) => entries.forEach((e) => e.isIntersecting && (e.target.classList.add('dw-near'), nearIO.unobserve(e.target))),
    { rootMargin: '600px 0px' }
  );
  // items inside horizontal scrollers also wait until they are actually on screen
  const itemIO = new IntersectionObserver(
    (entries) =>
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        e.target.dataset.seen = '1';
        itemIO.unobserve(e.target);
        if (e.target.closest('.dw-sec')?.dataset.seen) e.target.classList.add('in');
      }),
    { threshold: 0.06 }
  );

  function setLive(sec, live) {
    if (sec.classList.contains('is-live') === live) return;
    sec.classList.toggle('is-live', live);
    const api = inited.get(sec);
    if (live && !sec.dataset.seen) {
      sec.dataset.seen = '1';
      $$('.dw-ent', sec).forEach((el) => {
        if (el.closest('.dw-hs') && !el.dataset.seen && !desktop.matches) return;
        el.classList.add('in');
      });
      api?.onSeen?.();
    }
    api?.onLive?.(live);
  }

  /* ------------------------------------------------------ helpers */
  function tiltify(el) {
    if (reduce) return () => {};
    let last = el.scrollLeft, cur = 0, tgt = 0, raf = 0;
    const step = () => {
      cur += (tgt - cur) * 0.18;
      tgt *= 0.86;
      el.style.setProperty('--v', cur.toFixed(3));
      if (Math.abs(cur) > 0.005 || Math.abs(tgt) > 0.005) raf = requestAnimationFrame(step);
      else { el.style.setProperty('--v', 0); raf = 0; }
    };
    const onScroll = () => {
      const d = el.scrollLeft - last;
      last = el.scrollLeft;
      tgt = Math.max(-1, Math.min(1, d / 26));
      if (!raf) raf = requestAnimationFrame(step);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => { el.removeEventListener('scroll', onScroll); cancelAnimationFrame(raf); };
  }

  function bindDots(scroller, dotsEl, n) {
    if (!dotsEl) return () => {};
    n = n || scroller.querySelectorAll('.dw-track > *').length;
    const count = Math.min(n, 3);
    dotsEl.innerHTML = Array.from({ length: count }, (_, i) => `<button type="button" aria-label="Go to slide group ${i + 1}" data-i="${i}"></button>`).join('');
    const dots = $$('button', dotsEl);
    let raf = 0;
    const set = () => {
      raf = 0;
      const max = scroller.scrollWidth - scroller.clientWidth;
      dotsEl.hidden = max <= 4;
      const k = Math.round((max > 0 ? scroller.scrollLeft / max : 0) * (count - 1));
      dots.forEach((d, i) => d.classList.toggle('on', i === k));
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(set); };
    const onClick = (e) => {
      const i = +e.target.dataset.i;
      if (Number.isNaN(i)) return;
      const max = scroller.scrollWidth - scroller.clientWidth;
      scroller.scrollTo({ left: (max * i) / Math.max(1, count - 1), behavior: reduce ? 'auto' : 'smooth' });
    };
    scroller.addEventListener('scroll', onScroll, { passive: true });
    dotsEl.addEventListener('click', onClick);
    addEventListener('resize', onScroll);
    set();
    return () => { scroller.removeEventListener('scroll', onScroll); dotsEl.removeEventListener('click', onClick); removeEventListener('resize', onScroll); };
  }

  /* ------------------------------------------------------ fan carousel */
  class Fan {
    constructor(root, { stage, items, dotsEl, prev, next, autoplay = 0, sec }) {
      Object.assign(this, { root, stage, items, dotsEl, prev, next, autoplay, sec });
      this.idx = 0; this.hold = false; this.timer = 0; this.n = items.length;
      root.classList.add('dw-fan'); stage.classList.add('dw-fan__stage');
      items.forEach((it) => it.classList.add('dw-fan__item'));
      if (dotsEl) {
        dotsEl.innerHTML = items.map((_, i) => `<button type="button" aria-label="Show item ${i + 1}" data-i="${i}"></button>`).join('');
        dotsEl.hidden = this.n < 2;
      }
      this.h = {
        prev: () => { this.go(-1); this.play(); },
        next: () => { this.go(1); this.play(); },
        dot: (e) => { const i = +e.target.dataset.i; if (!Number.isNaN(i)) { this.idx = i; this.place(); this.play(); } },
        down: (e) => { this.sx = e.clientX; this.hold = true; },
        up: (e) => {
          if (this.sx == null) return;
          const dx = e.clientX - this.sx; this.sx = null; this.hold = false;
          if (Math.abs(dx) > 28) { this.go(dx < 0 ? 1 : -1); this.play(); this.swiped = true; setTimeout(() => (this.swiped = false), 0); }
        },
        click: (e) => {
          if (this.swiped) { e.preventDefault(); e.stopPropagation(); return; }
          const it = e.target.closest('.dw-fan__item');
          if (it && it.dataset.slot !== '0') { e.preventDefault(); this.idx = this.items.indexOf(it); this.place(); this.play(); }
        },
        enter: () => (this.hold = true),
        leave: () => { if (this.sx == null) this.hold = false; },
        key: (e) => { if (e.key === 'ArrowLeft') this.h.prev(); if (e.key === 'ArrowRight') this.h.next(); },
      };
      prev?.addEventListener('click', this.h.prev);
      next?.addEventListener('click', this.h.next);
      dotsEl?.addEventListener('click', this.h.dot);
      root.addEventListener('pointerdown', this.h.down);
      addEventListener('pointerup', this.h.up);
      root.addEventListener('click', this.h.click, true);
      root.addEventListener('mouseenter', this.h.enter);
      root.addEventListener('mouseleave', this.h.leave);
      root.addEventListener('keydown', this.h.key);
      this.place(); this.play();
    }
    slotOf(i) {
      let d = (i - this.idx + this.n) % this.n;
      if (d > this.n / 2) d -= this.n;
      return Math.abs(d) <= 2 ? String(d) : 'far';
    }
    place() {
      this.items.forEach((it, i) => {
        const s = this.slotOf(i);
        it.dataset.slot = s;
        it.setAttribute('aria-hidden', s === '0' ? 'false' : 'true');
        $$('a,button', it).forEach((f) => (f.tabIndex = s === '0' ? 0 : -1));
      });
      if (this.dotsEl) $$('button', this.dotsEl).forEach((d, i) => d.classList.toggle('on', i === this.idx));
    }
    go(d) { this.idx = (this.idx + d + this.n) % this.n; this.place(); }
    to(i) { this.idx = i; this.place(); this.play(); }
    play() {
      clearInterval(this.timer);
      if (reduce || !this.autoplay || this.n < 2) return;
      this.timer = setInterval(() => {
        if (this.sec.classList.contains('is-live') && !this.hold && !document.hidden) this.go(1);
      }, this.autoplay);
    }
    destroy() {
      clearInterval(this.timer);
      this.prev?.removeEventListener('click', this.h.prev);
      this.next?.removeEventListener('click', this.h.next);
      this.dotsEl?.removeEventListener('click', this.h.dot);
      this.root.removeEventListener('pointerdown', this.h.down);
      removeEventListener('pointerup', this.h.up);
      this.root.removeEventListener('click', this.h.click, true);
      this.root.removeEventListener('mouseenter', this.h.enter);
      this.root.removeEventListener('mouseleave', this.h.leave);
      this.root.removeEventListener('keydown', this.h.key);
      // slots/classes stay (rendered server-side); only restore focusability for the swipe layout
      this.items.forEach((it) => { it.removeAttribute('aria-hidden'); $$('a,button', it).forEach((f) => f.removeAttribute('tabindex')); });
    }
  }

  /* ------------------------------------------------------ cart */
  const cart = {
    total: null,
    async add(variantId, qty = 1) {
      const fd = new FormData();
      fd.append('id', variantId);
      fd.append('quantity', qty);
      fd.append('sections', 'cart-items,cart-footer,cart-item-count,cart-live-region');
      fd.append('sections_url', location.pathname);
      const url = (window.theme && theme.routes && theme.routes.cart_add_url) || '/cart/add.js';
      const res = await fetch(url, { method: 'POST', body: fd, headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' } });
      const data = await res.json();
      if (data.status) throw new Error(data.description || data.message || 'Could not add to bag');
      // hand over to the theme: updates count, drawer contents, milestones and opens the drawer
      document.body.dispatchEvent(new CustomEvent('baseline:cart:afteradditem', { bubbles: true, detail: { response: data, sourceId: 'dw-landing' } }));
      return data;
    },
    async refresh() {
      try {
        const r = await fetch(((window.theme && theme.routes && theme.routes.cart_url) || '/cart') + '.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
        const c = await r.json();
        cart.total = c.total_price;
        document.dispatchEvent(new CustomEvent('dw:cart', { detail: c }));
      } catch (e) { /* network hiccup: keep last known total */ }
    },
  };
  DW.cart = cart;
  ['baseline:cart:afteradditem', 'baseline:cart:cartqtychange', 'baseline:cart:update'].forEach((ev) => {
    window.addEventListener(ev, () => cart.refresh());
  });

  document.addEventListener('click', async (e) => {
    const b = e.target.closest('[data-dw-add]');
    if (!b || b.disabled || b.classList.contains('is-busy')) return;
    e.preventDefault();
    b.classList.add('is-busy');
    try {
      await cart.add(b.dataset.dwAdd);
      b.classList.add('is-done');
      const bag = document.querySelector('[data-cart-count], .cart-count, [href$="/cart"]');
      bag?.animate?.([{ transform: 'scale(1)' }, { transform: 'scale(1.35) rotate(-8deg)' }, { transform: 'scale(1)' }], { duration: 550, easing: 'cubic-bezier(.3,1.3,.42,1)' });
      setTimeout(() => b.classList.remove('is-done'), 1800);
    } catch (err) {
      const lbl = b.querySelector('.lbl');
      const orig = lbl.textContent;
      lbl.textContent = err.message.length < 28 ? err.message : 'Try again';
      setTimeout(() => (lbl.textContent = orig), 2200);
    } finally {
      b.classList.remove('is-busy');
    }
  });

  /* ------------------------------------------------------ section types */
  const T = {};

  T.hero = (sec) => {
    const zoom = $('.dw-hero__zoom', sec);
    const cta = $('.dw-cta', sec);
    const offs = [];
    // gold dust
    const dust = $('.dw-hero__dust', sec);
    if (dust && !reduce && dust.dataset.count) {
      let h = '';
      const n = +dust.dataset.count;
      for (let i = 0; i < n; i++) {
        const x = ((i * 61 + 23) % 360) / 3.9 + 3, y = ((i * 97 + 180) % 380) / 5.53 + 16, s = 2 + (i % 3);
        const d = 6 + ((i * 1.3) % 6), dl = -((i * 0.9) % 8), dx = (i % 2 ? 1 : -1) * (8 + (i % 5) * 4);
        h += `<span class="dw-dust" style="left:${x.toFixed(1)}%;top:${y.toFixed(1)}%;width:${s}px;height:${s}px;--d:${d.toFixed(1)}s;--dl:${dl.toFixed(1)}s;--dx:${dx}px"></span>`;
      }
      dust.innerHTML = h;
    }
    // parallax: only while the hero is on screen
    if (zoom && !reduce && sec.dataset.parallax === 'true') {
      let tick = false, on = true;
      const io = new IntersectionObserver(([e]) => (on = e.isIntersecting));
      io.observe(sec);
      const onScroll = () => {
        if (tick || !on) return;
        tick = true;
        requestAnimationFrame(() => {
          zoom.style.translate = `0 ${Math.min(scrollY * 0.05, 24).toFixed(1)}px`;
          tick = false;
        });
      };
      addEventListener('scroll', onScroll, { passive: true });
      offs.push(() => { removeEventListener('scroll', onScroll); io.disconnect(); });
    }
    if (cta && cta.getAttribute('href') === '#dw-next') {
      const go = (e) => {
        e.preventDefault();
        let n = sec.closest('.shopify-section')?.nextElementSibling;
        while (n && !n.offsetHeight) n = n.nextElementSibling;
        (n || sec).scrollIntoView({ behavior: reduce ? 'auto' : 'smooth' });
      };
      cta.addEventListener('click', go);
      offs.push(() => cta.removeEventListener('click', go));
    }
    return { destroy: () => offs.forEach((f) => f()) };
  };

  const scrollerSection = (sec) => {
    const offs = [];
    $$('.dw-hs', sec).forEach((hs) => {
      if (hs.classList.contains('dw-tilt-src')) offs.push(tiltify(hs));
      const dots = hs.parentElement.querySelector(':scope > .dw-dots');
      if (dots && sec.dataset.dw !== 'six') offs.push(bindDots(hs, dots));
      $$('.dw-ent', hs).forEach((el) => itemIO.observe(el));
    });
    return offs;
  };

  T.deck = T.pick = (sec) => {
    const offs = scrollerSection(sec);
    return { destroy: () => offs.forEach((f) => f()) };
  };

  T.sale = (sec) => {
    const offs = scrollerSection(sec);
    const revealIO = new IntersectionObserver((entries) => {
      entries.forEach((entry) => entry.target.classList.toggle('in', entry.isIntersecting));
    }, { threshold: 0.06 });
    $$('.dw-ent', sec).forEach((el) => revealIO.observe(el));
    return {
      destroy: () => { revealIO.disconnect(); offs.forEach((f) => f()); },
    };
  };

  T.dod = (sec) => {
    const root = $('.dw-fan-root', sec);
    const items = $$('.dw-fan-item', root);
    if (!items.length) return {};
    const fan = new Fan(root, {
      sec, stage: $('.dw-fan-stage', root), items,
      dotsEl: $('.dw-dots', sec), prev: $('.dw-fan__nav--p', root), next: $('.dw-fan__nav--n', root),
      autoplay: +sec.dataset.autoplay || 0,
    });
    return { destroy: () => fan.destroy(), select: (i) => fan.to(i) };
  };

  T.six = (sec) => {
    const offs = scrollerSection(sec);
    let lastScrollY = window.scrollY;
    let scrollingUp = false;
    const onScroll = () => {
      const scrollY = window.scrollY;
      scrollingUp = scrollY < lastScrollY;
      lastScrollY = scrollY;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    const revealIO = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && scrollingUp) sec.classList.add('is-scroll-reveal');
      else if (!entry.isIntersecting) sec.classList.remove('is-scroll-reveal');
    });
    revealIO.observe(sec);
    const hs = $('.dw-cards-hs', sec);
    const dots = $('.dw-dots', sec);
    let fan = null;
    const mode = () => {
      if (!hs || fan) return;
      fan = new Fan(hs, {
        sec, stage: $('.dw-track', hs), items: $$('.dw-fan__item', hs), dotsEl: dots,
        prev: $('.dw-fan__nav--p', hs), next: $('.dw-fan__nav--n', hs), autoplay: 0,
      });
      $$('.dw-ent', hs).forEach((el) => el.classList.add('in'));
    };
    mode();
    // countdown — daily window in the store's timezone
    const tEl = $('.dw-timer', sec);
    let timer = 0;
    const paintDigit = (el, v) => {
      if (el.textContent === v) return;
      el.textContent = v;
      if (reduce) return;
      el.classList.remove('dw-tick'); void el.offsetWidth; el.classList.add('dw-tick');
    };/*
    const tick = () => {
      const start = +tEl.dataset.start, end = +tEl.dataset.end, tz = +tEl.dataset.tz;
      const now = new Date(Date.now() + tz * 60000); // shifted so UTC getters read store-local time
      const mins = now.getUTCHours() * 60 + now.getUTCMinutes(), secs = now.getUTCSeconds();
      const s = start * 60, e = (end === 0 ? 24 : end) * 60;
      const within = s < e ? mins >= s && mins < e : mins >= s || mins < e;
      let left = within ? e - mins : s - mins;
      if (left <= 0) left += 1440;
      const total = left * 60 - secs;
      $('.dw-timer__l', tEl).textContent = within ? tEl.dataset.lEnds : tEl.dataset.lStarts;
      paintDigit($('[data-h]', tEl), String(Math.floor(total / 3600)).padStart(2, '0'));
      paintDigit($('[data-m]', tEl), String(Math.floor((total % 3600) / 60)).padStart(2, '0'));
    };*/
    const tick = () => {
      const tz = +tEl.dataset.tz;
      const now = new Date(Date.now() + tz * 60000);
      const hours = now.getUTCHours();
      const minutes = now.getUTCMinutes();
      const seconds = now.getUTCSeconds();
      // 6-hour reset: 00, 06, 12, 18
      const elapsed =
        (hours % 6) * 3600 +
        minutes * 60 +
        seconds;
      const total = 6 * 3600 - elapsed;
      const h = Math.floor(total / 3600);
      const m = Math.floor((total % 3600) / 60);
      const hEl = $('[data-h]', tEl);
      const mEl = $('[data-m]', tEl);
      if (hEl) {
        paintDigit(hEl, String(h).padStart(2, '0'));
      }
      if (mEl) {
        paintDigit(mEl, String(m).padStart(2, '0'));
      }
      const labelEl = $('.dw-timer__l', tEl);
      if (labelEl) {
        labelEl.textContent = 'Ends in';
      }
    };
    if (tEl) { tick(); timer = setInterval(() => sec.classList.contains('is-live') && tick(), 1000); }
    return {
      destroy: () => { clearInterval(timer); fan?.destroy(); revealIO.disconnect(); window.removeEventListener('scroll', onScroll); sec.classList.remove('is-scroll-reveal'); offs.forEach((f) => f()); },
      select: (i) => fan?.to(i),
    };
  };

  T.bag = (sec) => {
    const offs = scrollerSection(sec);
    const tiers = $$('.dw-ms__i', sec).map((el) => ({ el, amt: +el.dataset.amount, gift: el.dataset.label }));
    const fillEl = $('.dw-ms__fill', sec);
    const msg = $('.dw-bag__msg', sec);
    const cards = $$('.dw-gc', sec);
    // same output as Liquid's money_without_trailing_zeros, using the shop's money format
    const fmt = (c) => {
      const f = sec.dataset.moneyFormat || '₹{{amount}}';
      const whole = c % 100 === 0;
      const group = (n, d, t, sep) => { const [a, b] = n.toFixed(d).split('.'); return a.replace(/\B(?=(\d{3})+(?!\d))/g, t) + (b && !whole ? sep + b : ''); };
      return f.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => {
        const v = c / 100;
        if (k === 'amount_no_decimals') return group(v, 0, ',', '.');
        if (k === 'amount_with_comma_separator') return group(v, 2, '.', ',');
        if (k === 'amount_no_decimals_with_comma_separator') return group(v, 0, '.', ',');
        return group(v, 2, ',', '.');
      });
    };
    let seen = false, first = true;
    function burst(el) {
      const ic = $('.dw-ms__ic', el);
      for (let i = 0; i < 10; i++) {
        const s = document.createElement('b');
        s.className = 'dw-spk';
        const a = (i / 10) * Math.PI * 2 + Math.random() * 0.4, r = 22 + Math.random() * 12;
        s.style.setProperty('--x', Math.cos(a) * r + 'px');
        s.style.setProperty('--y', Math.sin(a) * r + 'px');
        ic.appendChild(s);
        setTimeout(() => s.remove(), 800);
      }
    }
    function render(total) { console.log('total : ', total);
      if (!seen || total == null || !tiers.length) return;
      const n = tiers.filter((t) => total >= t.amt * 100).length;
      // progress: piecewise so each milestone sits on its dot
      let p;
      if (n >= tiers.length) p = 1;
      else {
        const lo = n ? tiers[n - 1].amt * 100 : 0, hi = tiers[n].amt * 100;
        const seg = 1 / (tiers.length - 1);
        p = n === 0 ? 0 : (n - 1) * seg + seg * ((total - lo) / (hi - lo));
      }
      fillEl.style.setProperty('--p', Math.max(0, Math.min(1, p)).toFixed(4));
      tiers.forEach((t, i) => {
        const on = i < n, was = t.el.classList.contains('on');
        t.el.classList.toggle('on', on);
        if (on && !was && !reduce && !first) { t.el.classList.remove('pop'); void t.el.offsetWidth; t.el.classList.add('pop'); burst(t.el); }
      });
      first = false;
      const next = tiers[n]; 
      const txt = next ? sec.dataset.msg.replace('[amount]', '₹' + (next.amt - total / 100)).replace('[gift]', next.gift) : sec.dataset.msgDone;
      if (msg.textContent !== txt) {
        msg.classList.add('swap');
        setTimeout(() => { msg.textContent = txt; msg.classList.remove('swap'); }, 220);
      }
    }
    const onCart = (e) => render(e.detail.total_price);
    document.addEventListener('dw:cart', onCart);
    const onClick = (e) => {
      const li = e.target.closest('.dw-ms__i');
      if (!li) return;
      const i = tiers.findIndex((t) => t.el === li);
      const card = cards[i];
      if (!reduce) { li.classList.remove('pop'); void li.offsetWidth; li.classList.add('pop'); burst(li); }
      if (card) {
        const hs = card.closest('.dw-hs');
        if (hs && hs.scrollWidth > hs.clientWidth) hs.scrollTo({ left: card.offsetLeft - 16, behavior: reduce ? 'auto' : 'smooth' });
        card.classList.add('is-hit');
        setTimeout(() => card.classList.remove('is-hit'), 900);
      }
    };
    sec.addEventListener('click', onClick);
    return {
      onSeen: () => { seen = true; setTimeout(() => (cart.total == null ? cart.refresh() : render(cart.total)), 450); },
      destroy: () => { document.removeEventListener('dw:cart', onCart); sec.removeEventListener('click', onClick); offs.forEach((f) => f()); },
    };
  };

  T.testi = (sec) => {
    const offs = [];
    const hs = $('.dw-testi__hs', sec);
    const track = $('.dw-testi__track', sec);
    $$('.dw-tc__q', sec).forEach((q) => {
      if (q.dataset.split) return;
      q.dataset.split = '1';
      const words = q.textContent.trim().split(/\s+/);
      q.setAttribute('aria-label', q.textContent.trim());
      q.innerHTML = words.map((w, i) => `<span class="dw-w" aria-hidden="true"><span style="--d:${(0.05 + i * 0.05).toFixed(2)}s">${w.replace(/</g, '&lt;')}</span></span>`).join(' ');
    });
    const cardIO = new IntersectionObserver((es) => es.forEach((e) => e.isIntersecting && sec.dataset.seen && (e.target.classList.add('in'), cardIO.unobserve(e.target))), { threshold: 0.5 });
    const watch = () => $$('.dw-tc:not(.in)', sec).forEach((c) => cardIO.observe(c));
    let clones = [];
    const mode = () => {
      const cards = $$('.dw-tc:not([data-clone])', track);
      const want = desktop.matches && !reduce && cards.length > 1;
      if (want && !clones.length) {
        clones = cards.map((c) => { const k = c.cloneNode(true); k.dataset.clone = '1'; k.setAttribute('aria-hidden', 'true'); k.classList.add('in'); return k; });
        clones.forEach((k) => track.appendChild(k));
        track.style.setProperty('--dur', cards.length * 9 + 's');
        hs.classList.add('is-marquee');
      } else if (!want && clones.length) {
        clones.forEach((k) => k.remove()); clones = [];
        hs.classList.remove('is-marquee');
      }
    };
    mode();
    desktop.addEventListener('change', mode);
    const dots = $('.dw-dots', sec);
    if (dots) offs.push(bindDots(hs, dots));
    return {
      onSeen: () => { watch(); if (desktop.matches) $$('.dw-tc', sec).forEach((c) => c.classList.add('in')); },
      destroy: () => { cardIO.disconnect(); desktop.removeEventListener('change', mode); offs.forEach((f) => f()); },
    };
  };

  T.faq = (sec) => {
    const single = sec.dataset.single === 'true';
    const onClick = (e) => {
      const q = e.target.closest('.dw-acc__q');
      if (!q) return;
      const item = q.parentElement, open = !item.classList.contains('open');
      if (single) $$('.dw-acc.open', sec).forEach((o) => { if (o !== item) { o.classList.remove('open'); $('.dw-acc__q', o).setAttribute('aria-expanded', 'false'); } });
      item.classList.toggle('open', open);
      q.setAttribute('aria-expanded', String(open));
    };
    sec.addEventListener('click', onClick);
    return { destroy: () => sec.removeEventListener('click', onClick), select: (i) => { const it = $$('.dw-acc', sec)[i]; if (it && !it.classList.contains('open')) $('.dw-acc__q', it).click(); } };
  };

  /* ------------------------------------------------------ lifecycle */
  function init(sec) {
    if (inited.has(sec)) return;
    const fn = T[sec.dataset.dw];
    const api = (fn && fn(sec)) || {};
    inited.set(sec, api);
    zoneIO.observe(sec);
    nearIO.observe(sec);
  }
  function destroy(sec) {
    const api = inited.get(sec);
    if (!api) return;
    api.destroy?.();
    zoneIO.unobserve(sec);
    nearIO.unobserve(sec);
    inited.delete(sec);
  }
  DW.init = init;
  const boot = () => $$('.dw-sec[data-dw]').forEach(init);
  boot();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);

  // theme editor
  document.addEventListener('shopify:section:load', (e) => $$('.dw-sec[data-dw]', e.target).forEach((s) => { init(s); s.dataset.seen = ''; setLive(s, false); requestAnimationFrame(() => { zoneIO.unobserve(s); zoneIO.observe(s); }); }));
  document.addEventListener('shopify:section:unload', (e) => $$('.dw-sec[data-dw]', e.target).forEach(destroy));
  document.addEventListener('shopify:block:select', (e) => {
    const sec = e.target.closest('.dw-sec[data-dw]');
    const api = sec && inited.get(sec);
    if (!api?.select) return;
    const blocks = $$('[data-block-index]', sec).filter((b) => !b.dataset.clone);
    const i = blocks.indexOf(e.target.closest('[data-block-index]'));
    if (i > -1) api.select(i);
  });
}